// ISDP CUSTOM: TechDocs reader addons bundle.
//
// This file provides SIX independent TechDocs reader addons (all registered on
// techdocsCustomAddonsPlugin): responsive drawer styles, copy-permalink, download
// button, author byline, admin "view source", and the preview/draft banner. They
// share this module because they all patch the same TechDocs reader surface.
//
// --- Responsive drawer styles addon ---
// Backstage rehosts the mkdocs-material HTML inside a shadow DOM and emulates the
// sidebars with fixed positioning. At narrow viewports (the mkdocs "drawer"
// breakpoint, <= 76.1875em) that emulation breaks: the primary nav sidebar is
// pinned at left:-32px with transform:translateX(0) instead of being tucked fully
// off-canvas, so it overlaps the article and clips the content when the window is
// at ~half width.
//
// We can't reach the shadow DOM from odx-overrides.css (its mkdocs stylesheet is
// loaded cross-origin from the backend), so we inject a corrective <style> into the
// shadow root via a TechDocs addon. It restores proper drawer behaviour: the nav is
// hidden off-canvas by default and slides in only when the header hamburger toggles
// the #__drawer checkbox.
import { useEffect } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { createPlugin } from '@backstage/core-plugin-api';
import {
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
  useShadowRoot,
  useTechDocsReaderPage,
} from '@backstage/plugin-techdocs-react';
import { useToast } from '../feedback/ToastProvider';
import { getAuthor, isPreview } from '../catalog/entityHelpers';
import { DocsIconNudge } from '../catalog/DocsIconNudge';
import { SourceLink } from '../common/SourceButton';
import './techDocsSubheader.css';

const STYLE_ID = 'devhub-techdocs-responsive';

const RESPONSIVE_CSS = `
/* Hide mkdocs-material's repo chrome — the "Edit this page" pencil and the
   "Go to repository" source link — which appear on docs whose repo sets
   edit_uri/repo_url and take the reader straight to the source repo. Internal
   readers shouldn't be pushed to the repo from the docs view (admins still have
   the dedicated "View source" control). The edit pencil is an <a>, so scoping to
   the anchor leaves the sidebar-toggle <label> (same class) untouched. */
a.md-content__button,
.md-source {
  display: none !important;
}

/* The Curator launcher floats fixed in the bottom-right of the MAIN document
   (~84px tall), so the mkdocs "Prev / Next" footer nav — which sits at the very
   bottom-right of the doc — ends up hidden under the bot when scrolled to the end.
   Give the footer bottom clearance so the Next button always sits above it. */
.md-footer {
  margin-bottom: 96px !important;
}

/* Drawer breakpoint — primary nav becomes an off-canvas drawer. Backstage's
   fixed-position emulation leaves it peeking over the content; tuck it fully away
   and only reveal it when the hamburger toggles the drawer. */
@media screen and (max-width: 76.1875em) {
  .md-sidebar--primary {
    left: 0 !important;
    /* A drawer needs to read as a SHEET, not as loose text over the article. Without a surface,
       elevation and a usable width it looked like a small popup floating in the corner. mkdocs' own
       256px is cramped once ToC entries wrap, so take a share of narrow viewports instead.
       --md-default-bg-color is the reader's own surface token, so this follows light/dark. */
    width: min(320px, 86vw) !important;
    background-color: var(--md-default-bg-color) !important;
    box-shadow: 4px 0 16px rgba(0, 0, 0, 0.18) !important;
    transform: translateX(-100%) !important;
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
    overflow: hidden !important;
    z-index: 300 !important;
  }
  /* The drawer is taller than the viewport on long docs, so its contents must scroll. Its inner
     wrapper keeps mkdocs' fixed 256px, which would leave a dead strip inside the wider sheet — let it
     fill the panel so long ToC entries wrap later. */
  .md-sidebar--primary .md-sidebar__scrollwrap {
    overflow-y: auto !important;
    max-height: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }
  [data-md-toggle="drawer"]:checked ~ .md-container .md-sidebar--primary {
    transform: translateX(0) !important;
  }
  /* Dim-to-dismiss backdrop behind the open nav drawer. NOTE the selector: .md-overlay is a SIBLING
     of .md-container (both are direct children of body), not a descendant — the previous
     "~ .md-container .md-overlay" could never match, so this rule was dead and the drawer opened with
     no scrim. Sits below the sidebar (z-index 300) and closes the drawer when tapped. */
  [data-md-toggle="drawer"]:checked ~ .md-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    z-index: 290 !important;
    background-color: rgba(0, 0, 0, 0.45) !important;
    opacity: 1 !important;
    transition: opacity 0.25s !important;
  }
  /* Let the article use the full width once the drawer is out of the flow. */
  .md-content {
    margin-left: 0 !important;
    max-width: 100% !important;
  }
}

@media print, screen and (prefers-reduced-motion: reduce) {
  .md-sidebar--primary {
    transition: none !important;
  }
}
`;

// ISDP CUSTOM: in LIGHT mode the doc reader inherited the app canvas colour
// (--md-default-bg-color = theme background.default = #f5f7fa), a faint cool grey
// that reads as "blue" behind the doc text — unlike the rest of the app, where
// content sits on white cards over that canvas. Force the reader's default surface
// to white in light mode only. Dark mode keeps its themed dark surface (#0d1b2a),
// so this is applied conditionally (see isDark below), never in dark.
const LIGHT_SURFACE_CSS = `
body { --md-default-bg-color: #ffffff !important; }
`;

const TechDocsResponsiveStylesAddon = () => {
  const shadowRoot = useShadowRoot();
  // Reactive to theme toggles (MUI re-renders on theme change), so the surface
  // override is added/removed as the user switches light↔dark.
  const isDark = useTheme().palette.type === 'dark';

  useEffect(() => {
    if (!shadowRoot) {
      return undefined;
    }
    // Re-inject (not skip-if-present) so a light↔dark toggle updates the surface.
    shadowRoot.querySelector(`#${STYLE_ID}`)?.remove();
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = RESPONSIVE_CSS + (isDark ? '' : LIGHT_SURFACE_CSS);
    shadowRoot.appendChild(style);
    return () => {
      style.remove();
    };
  }, [shadowRoot, isDark]);

  return null;
};

// ISDP CUSTOM: Clean "copy link" control on headings.
//
// mkdocs-material renders a permalink anchor after each heading:
//   <a class="headerlink" href="…#anchor" title="Permanent link">¶</a>
// Out of the box the pilcrow (¶) is ugly and clicking it merely jumps to the
// anchor — it does not copy anything, which is what users expect from a
// "permalink" affordance. We replace the pilcrow glyph with a clean link icon
// (CSS, injected into the shadow root) and intercept clicks to copy the heading's
// deep-link URL to the clipboard, with a toast confirmation.
const COPYLINK_STYLE_ID = 'devhub-techdocs-copylink';

const COPYLINK_CSS = `
/* Hide the raw pilcrow glyph and draw a clean link icon in its place. */
.md-typeset .headerlink {
  font-size: 0 !important;               /* hides the ¶ text node */
  user-select: none !important;          /* keep the ¶ out of manual text selection / copy */
  -webkit-user-select: none !important;
  /* Fixed px sizing — em would collapse to 0 because font-size is 0. */
  width: 16px !important;
  height: 16px !important;
  margin-left: 0.4rem;
  vertical-align: middle;
  display: inline-block !important;
  color: var(--md-default-fg-color--lighter, currentColor);
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
  background-color: currentColor;
  /* Material "link" glyph */
  -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1M8 13h8v-2H8zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5'/%3E%3C/svg%3E");
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1M8 13h8v-2H8zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5'/%3E%3C/svg%3E");
  cursor: pointer;
  transition: color 0.15s ease, transform 0.1s ease;
}
.md-typeset .headerlink:hover {
  color: var(--cyan-500, #0091f7) !important;
}
.md-typeset .headerlink:active {
  transform: scale(0.88);
}
`;

const CopyPermalinkAddon = () => {
  const shadowRoot = useShadowRoot();
  const toast = useToast();

  useEffect(() => {
    if (!shadowRoot) {
      return undefined;
    }

    // Inject the icon styling once.
    if (!shadowRoot.querySelector(`#${COPYLINK_STYLE_ID}`)) {
      const style = document.createElement('style');
      style.id = COPYLINK_STYLE_ID;
      style.textContent = COPYLINK_CSS;
      shadowRoot.appendChild(style);
    }

    const copy = async (url: string) => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
        } else {
          // Fallback for insecure contexts / older browsers.
          const ta = document.createElement('textarea');
          ta.value = url;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          ta.remove();
        }
        toast.success('Link copied to clipboard');
      } catch {
        toast.error('Could not copy link');
      }
    };

    const onClick = (event: Event) => {
      const target = event.target as HTMLElement | null;
      const link = target?.closest?.('.headerlink') as HTMLAnchorElement | null;
      if (!link) {
        return;
      }
      // Copy instead of the default in-page jump.
      event.preventDefault();
      const href = link.href || link.getAttribute('href') || '';
      // Still update the address bar hash so the URL reflects the anchor.
      try {
        const hash = new URL(href, window.location.href).hash;
        if (hash) {
          window.history.replaceState(null, '', hash);
        }
      } catch {
        /* ignore malformed hrefs */
      }
      // Fire and forget; copy() handles its own toast feedback.
      void copy(href);
    };

    // Give each permalink an accessible, hover title.
    shadowRoot.querySelectorAll('.headerlink').forEach(el => {
      el.setAttribute('title', 'Copy link to this section');
      el.setAttribute('aria-label', 'Copy link to this section');
    });

    shadowRoot.addEventListener('click', onClick);
    return () => {
      shadowRoot.removeEventListener('click', onClick);
    };
  }, [shadowRoot, toast]);

  return null;
};

// ISDP CUSTOM: Download button for files shipped in a repo's docs/assets/.
//
// Goal: a team drops a file in their repo's `docs/assets/` and writes, in
// markdown, a styled download link using mkdocs-material's button classes plus
// the `attr_list` extension:
//
//   [Download the PDF](assets/sample.pdf){ .md-button .md-button--primary download }
//
// Two problems this addon solves:
//   1. Styling — we give `.md-button` an on-brand (ODX) look inside the shadow
//      root (the mkdocs-material stylesheet is loaded cross-origin so we can't
//      reach it from odx-overrides.css).
//   2. Navigation — the TechDocs reader installs its own capture-phase click
//      handler that intercepts in-page <a> clicks and turns them into SPA
//      navigations. For a plain download link that means the reader tries to
//      "navigate" to the PDF and breaks the download. We therefore add our OWN
//      capture-phase listener (which runs before the reader's, being attached to
//      the shadow root first / at capture) that, for links we recognise as
//      downloads, cancels the event, fetches the target as a blob, and triggers
//      a real browser save. Falls back to window.open on any fetch error.
const DOWNLOAD_STYLE_ID = 'devhub-techdocs-download';

// Binary/attachment file extensions we treat as downloads even without the
// explicit `download` attribute.
const DOWNLOAD_EXTENSIONS = [
  '.pdf',
  '.zip',
  '.docx',
  '.doc',
  '.xlsx',
  '.xls',
  '.csv',
  '.pptx',
  '.ppt',
  '.7z',
  '.tar',
  '.gz',
  '.rtf',
];

// Extensions that are pages (or page-like) and must keep normal navigation.
const PAGE_EXTENSIONS = new Set(['html', 'htm', 'md', 'php', 'aspx']);

const DOWNLOAD_CSS = `
/* On-brand ODX styling for mkdocs-material .md-button download affordances. */
.md-typeset .md-button {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border-radius: 6px;
  border: 1px solid var(--cyan-500, #0091f7);
  color: var(--cyan-500, #0091f7);
  background: transparent;
  padding: 0.5em 1.1em;
  font-weight: 600;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}
.md-typeset .md-button:hover,
.md-typeset .md-button:focus {
  background: var(--cyan-500, #0091f7);
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 145, 247, 0.28);
}
.md-typeset .md-button--primary {
  background: var(--cyan-500, #0091f7);
  color: #fff;
  border-color: var(--cyan-500, #0091f7);
}
.md-typeset .md-button--primary:hover,
.md-typeset .md-button--primary:focus {
  background: var(--cyan-600, #0077cc);
  border-color: var(--cyan-600, #0077cc);
  color: #fff;
}
/* Download glyph before recognised download buttons. */
.md-typeset a.md-button[download]::before,
.md-typeset a.md-button[data-devhub-download]::before {
  content: '';
  width: 16px;
  height: 16px;
  display: inline-block;
  background-color: currentColor;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M5 20h14v-2H5zM19 9h-4V3H9v6H5l7 7z'/%3E%3C/svg%3E");
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M5 20h14v-2H5zM19 9h-4V3H9v6H5l7 7z'/%3E%3C/svg%3E");
}
`;

// Decide whether an anchor should be handled as a download rather than SPA nav.
// Exported for unit testing.
export const isDownloadLink = (link: HTMLAnchorElement): boolean => {
  if (link.hasAttribute('download')) {
    return true;
  }
  const href = (link.getAttribute('href') || '').toLowerCase();
  if (!href || href.startsWith('#')) {
    return false;
  }
  // .md-button pointing at an asset path.
  if (link.classList.contains('md-button') && href.includes('assets/')) {
    return true;
  }
  // Strip query/hash before matching the extension.
  const clean = href.split('?')[0].split('#')[0];
  if (DOWNLOAD_EXTENSIONS.some(ext => clean.endsWith(ext))) return true;
  // Any other FILE inside the doc's own site — a config snippet (.json, .toml, .yaml, .sh,
  // .rules, …) linked as `[download](../configs/x.json)`. Pages are directories (`…/`) or
  // `.html`; anything else with an extension is a file, and following it in the SPA lands on
  // "page not found". Only same-site links: an off-site file can't be fetched (CORS), so a
  // normal navigation is the right thing there.
  if (/^[a-z][a-z0-9+.-]*:|^\/\//.test(clean) && !clean.startsWith(window.location.origin)) {
    return false;
  }
  const last = clean.split('/').filter(Boolean).pop() ?? '';
  const dot = last.lastIndexOf('.');
  if (dot <= 0 || clean.endsWith('/')) return false;
  return !PAGE_EXTENSIONS.has(last.slice(dot + 1));
};

// Where TechDocs serves a doc's static assets from.
const TECHDOCS_STATIC_PREFIX = '/api/techdocs/static/docs';

// The two in-app routes the reader renders under, capturing namespace/kind/name and the
// path tail. `/docs/:ns/:kind/:name/<tail>` and `/catalog/:ns/:kind/:name/documentation/<tail>`.
const READER_ROUTE =
  /^\/(?:docs\/([^/]+)\/([^/]+)\/([^/]+)|catalog\/([^/]+)\/([^/]+)\/([^/]+)\/documentation)\/(.+)$/;

/**
 * Maps an asset href that was resolved against the SPA route back onto the TechDocs static
 * route. Returns null when the href needs no correction (already static, or off-site).
 *
 * WHY THIS IS NEEDED. Backstage's `addBaseUrl` reader transformer only rewrites
 * `img[src]`, `script[src]`, `source[src]`, `link[href]` and — crucially — `a[download]`.
 * A plain `[PDF](assets/x.pdf)` carries no `download` attribute, so it is left to the next
 * transformer (`rewriteDocLinks`), which resolves it against `window.location.href`. The
 * result is an in-app route like
 *   /catalog/default/component/partner-medibus-docs/documentation/assets/x.pdf
 * which the SPA answers with `index.html` and a 200. Fetching that "succeeds", so the app
 * shell was being saved under a .pdf name — with a success toast. Exported for testing.
 */
export const techdocsAssetUrl = (href: string): string | null => {
  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return null;
  }
  // Leave anything cross-origin alone, and anything already pointing at the static route.
  if (url.origin !== window.location.origin) return null;
  if (url.pathname.startsWith(TECHDOCS_STATIC_PREFIX)) return null;

  const m = READER_ROUTE.exec(url.pathname);
  if (!m) return null;
  const namespace = m[1] ?? m[4];
  const kind = m[2] ?? m[5];
  const name = m[3] ?? m[6];
  const tail = m[7];
  if (!namespace || !kind || !name || !tail) return null;
  // The static tree mirrors the doc tree, so the tail carries over verbatim.
  return `${TECHDOCS_STATIC_PREFIX}/${namespace}/${kind}/${name}/${tail}${url.search}`;
};

// Derive a sensible filename from an href. Exported for unit testing.
export const filenameFromHref = (href: string): string => {
  try {
    const url = new URL(href, window.location.href);
    const parts = url.pathname.split('/').filter(Boolean);
    return decodeURIComponent(parts[parts.length - 1] || 'download');
  } catch {
    const parts = href.split('#')[0].split('?')[0].split('/').filter(Boolean);
    return parts[parts.length - 1] || 'download';
  }
};

const TechDocsDownloadButtonAddon = () => {
  const shadowRoot = useShadowRoot();
  const toast = useToast();

  useEffect(() => {
    if (!shadowRoot) {
      return undefined;
    }

    // Inject the button styling once.
    if (!shadowRoot.querySelector(`#${DOWNLOAD_STYLE_ID}`)) {
      const style = document.createElement('style');
      style.id = DOWNLOAD_STYLE_ID;
      style.textContent = DOWNLOAD_CSS;
      shadowRoot.appendChild(style);
    }

    // Normalise every recognised download anchor, not just the styled buttons:
    //  • point it at the TechDocs static route (see techdocsAssetUrl), and
    //  • give it the `download` attribute.
    // Doing this to the DOM — rather than only inside the click handler — is what makes
    // middle-click, Ctrl+click and "Save link as" work, and the `download` attribute also
    // makes Backstage's own link interceptor skip the anchor (it exempts `[download]`), so
    // correctness no longer depends on winning a capture-phase race.
    shadowRoot.querySelectorAll('a[href]').forEach(el => {
      const a = el as HTMLAnchorElement;
      if (!isDownloadLink(a)) return;
      const corrected = techdocsAssetUrl(a.href);
      if (corrected) a.setAttribute('href', corrected);
      if (!a.hasAttribute('download')) a.setAttribute('download', '');
      // Tag so the CSS glyph applies to styled buttons.
      if (a.classList.contains('md-button')) {
        a.setAttribute('data-devhub-download', '');
      }
    });

    const triggerSave = (blobUrl: string, filename: string) => {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    };

    const download = async (absoluteUrl: string, filename: string) => {
      try {
        const res = await fetch(absoluteUrl, { credentials: 'include' });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        // FAIL LOUDLY on the SPA shell. If the URL resolved to an in-app route, the SPA
        // answers with index.html and a 200 — so `res.ok` is true and we would happily
        // save a 12 KB HTML page as "spec.pdf" and report success. A download that
        // silently produces a corrupt file is worse than one that visibly fails.
        const type = res.headers.get('content-type') || '';
        if (/^text\/html/i.test(type) && !/\.html?$/i.test(filename)) {
          throw new Error(`served text/html, not a file (${absoluteUrl})`);
        }
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        triggerSave(blobUrl, filename);
        // Revoke on the next tick so the click has a chance to start the save.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
        toast.success(`Downloading ${filename}`);
      } catch {
        // Do NOT fall back to window.open here: for the failure this guards against, the
        // URL is an in-app route, so opening it would just render the app in a new tab and
        // look like a different bug. Say so instead.
        toast.error(`Couldn't download ${filename}`);
      }
    };

    const onClick = (event: Event) => {
      const target = event.target as HTMLElement | null;
      const link = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!link || !isDownloadLink(link)) {
        return;
      }
      // Stop the reader's own capture-phase handler from SPA-navigating.
      event.preventDefault();
      event.stopPropagation();
      // `link.href` is absolute, but for a plain (non-`download`) asset link it was
      // resolved against the SPA route rather than the TechDocs static route — see
      // techdocsAssetUrl. Correct it before fetching.
      const rawUrl = link.href || link.getAttribute('href') || '';
      const absoluteUrl = techdocsAssetUrl(rawUrl) ?? rawUrl;
      const filename = filenameFromHref(absoluteUrl);
      void download(absoluteUrl, filename);
    };

    // Capture phase so we run before the TechDocs reader's link interceptor.
    shadowRoot.addEventListener('click', onClick, true);
    return () => {
      shadowRoot.removeEventListener('click', onClick, true);
    };
  }, [shadowRoot, toast]);

  return null;
};

// ISDP CUSTOM: Author byline in the doc reader.
//
// Shows the doc's last human author (stamped by the backend LastAuthorProcessor
// onto draeger.com/last-author) in the reader subheader, so a reader knows who to
// contact about the page. Renders as plain React (Subheader location is main-DOM,
// not shadow DOM), reusing getAuthor() so the card byline and this stay identical.
const AuthorTagAddon = () => {
  const { entityMetadata } = useTechDocsReaderPage();
  const entity = entityMetadata.value;
  const author = entity ? getAuthor(entity) : null;
  if (!author) {
    return null;
  }
  return (
    <div className="devhub-techdocs-author">
      <span className="devhub-techdocs-author__label">Author</span>
      <span className="devhub-techdocs-author__name">{author.name}</span>
      {author.email && (
        <a
          className="devhub-techdocs-author__email"
          href={`mailto:${author.email}`}
        >
          {author.email}
        </a>
      )}
    </div>
  );
};

// ISDP CUSTOM: "View source" in the doc reader subheader — jumps to the docs'
// backing repo/file. Reuses the shared SourceLink (URL gated), so it renders nothing
// for docs with no known source.
const SourceLinkAddon = () => {
  const { entityMetadata } = useTechDocsReaderPage();
  return (
    <div className="devhub-techdocs-source">
      <SourceLink entity={entityMetadata.value} />
    </div>
  );
};

// ISDP CUSTOM: unmissable PREVIEW/DRAFT banner in the doc reader.
//
// A preview entity's rendered docs ARE viewable by direct link (so an author can
// check how the page looks) — but the doc is kept out of search, nav, the Curator
// and the MCP face, and this banner makes it impossible to mistake the draft for a
// published, official guideline. Renders as plain React in the Subheader (main DOM,
// not shadow DOM), gated on isPreview() — nothing at all for normal docs.
const PreviewBannerAddon = () => {
  const { entityMetadata } = useTechDocsReaderPage();
  const entity = entityMetadata.value;
  if (!entity || !isPreview(entity)) {
    return null;
  }
  return (
    <div
      role="alert"
      className="devhub-techdocs-preview-banner"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        margin: '4px 0 12px',
        padding: '10px 14px',
        borderRadius: 8,
        // Off-scale amber warning palette (matches the listing draft banner);
        // deliberately not an ODX token — this is a bespoke "unpublished" state.
        background: 'light-dark(#fff4d6, #3a3212)',
        color: 'light-dark(#6b4e00, #ffe08a)',
        border: '1px solid light-dark(#f0d99a, #5a4d1a)',
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      <odx-icon className="odx-icon" name="core::warning" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        <span>
          PREVIEW — this is an unpublished draft, not an official guideline. It may
          change or be removed, and is hidden from search.
        </span>
        <DocsIconNudge entity={entity} />
      </div>
    </div>
  );
};

const techdocsCustomAddonsPlugin = createPlugin({
  id: 'techdocs-custom-addons',
});

export const TechDocsResponsiveStyles = techdocsCustomAddonsPlugin.provide(
  createTechDocsAddonExtension({
    name: 'TechDocsResponsiveStyles',
    location: TechDocsAddonLocations.Content,
    component: TechDocsResponsiveStylesAddon,
  }),
);

export const TechDocsCopyPermalink = techdocsCustomAddonsPlugin.provide(
  createTechDocsAddonExtension({
    name: 'TechDocsCopyPermalink',
    location: TechDocsAddonLocations.Content,
    component: CopyPermalinkAddon,
  }),
);

export const TechDocsDownloadButton = techdocsCustomAddonsPlugin.provide(
  createTechDocsAddonExtension({
    name: 'TechDocsDownloadButton',
    location: TechDocsAddonLocations.Content,
    component: TechDocsDownloadButtonAddon,
  }),
);

export const TechDocsAuthorTag = techdocsCustomAddonsPlugin.provide(
  createTechDocsAddonExtension({
    name: 'TechDocsAuthorTag',
    location: TechDocsAddonLocations.Subheader,
    component: AuthorTagAddon,
  }),
);

export const TechDocsSourceLink = techdocsCustomAddonsPlugin.provide(
  createTechDocsAddonExtension({
    name: 'TechDocsSourceLink',
    location: TechDocsAddonLocations.Subheader,
    component: SourceLinkAddon,
  }),
);

export const TechDocsPreviewBanner = techdocsCustomAddonsPlugin.provide(
  createTechDocsAddonExtension({
    name: 'TechDocsPreviewBanner',
    location: TechDocsAddonLocations.Subheader,
    component: PreviewBannerAddon,
  }),
);
